import { registerErrorReporter } from './errorManager';

let sentryInitialized = false;
let sentryModulePromise: Promise<typeof import('@sentry/react')> | null = null;

function loadSentry() {
  if (!sentryModulePromise) {
    sentryModulePromise = import('@sentry/react');
  }
  return sentryModulePromise;
}

/**
 * Initialise Sentry and register it as an error reporter.
 *
 * Call after first paint via `scheduleNonCriticalStartup()` so Sentry does not
 * compete with LCP. Sentry is disabled in dev mode or when VITE_SENTRY_DSN is
 * not set — all SDK calls become safe no-ops in those cases.
 */
export function initSentry(): void {
  if (sentryInitialized) return;
  sentryInitialized = true;

  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (import.meta.env.DEV || !dsn) return;

  const release =
    (import.meta.env.VITE_SENTRY_RELEASE as string | undefined) ||
    (import.meta.env.VITE_APP_VERSION as string | undefined);

  void loadSentry().then((Sentry) => {
    Sentry.init({
      dsn,
      enabled: true,
      environment: import.meta.env.MODE,
      release,
      integrations: [Sentry.browserTracingIntegration()],
      tracesSampleRate: 0.1,
    });

    registerErrorReporter((err, context) => {
      Sentry.captureException(err, { extra: context });
    });
  });
}

/** Attach a user to all subsequent Sentry events. Call after login or session restore. */
export function setSentryUser(user: { id: string }): void {
  if (import.meta.env.DEV || !import.meta.env.VITE_SENTRY_DSN) return;
  void loadSentry().then((Sentry) => Sentry.setUser(user));
}

/** Detach the current user from Sentry events. Call on logout. */
export function clearSentryUser(): void {
  if (import.meta.env.DEV || !import.meta.env.VITE_SENTRY_DSN) return;
  void loadSentry().then((Sentry) => Sentry.setUser(null));
}
